import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Link, useNavigate } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik as useFormic } from "formik";

// Image
import logoLight from "assets/images/logo-light.png";
import logoDark from "assets/images/logo-dark.png";
import { RootState } from "slices";
import { updatePassword } from "slices/thunk";
import { ToastContainer } from "react-toastify";
import { Loader2 } from "lucide-react";

const UpdatePassword = (props: any) => {
  document.title =
    "Update Password | Tailwick - React Admin & Dashboard Template";

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectUpdatePassword = createSelector(
    (state: RootState) => state.passwordUpdate,
    (newPassword) => ({
      success: newPassword.isUpdateSuccess,
      error: newPassword.isUpdateError,
      isLoading: newPassword.isLoading,
    })
  );

  const { success, error, isLoading } = useSelector(selectUpdatePassword);

  const email = useSelector((state: RootState) => state.ForgotPassword.email);
  const otp = useSelector((state: RootState) => state.verifyOtp.otp);

  const validation: any = useFormic({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      newPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string().required("Please Enter Your New Password"),
    }),
    onSubmit: (values: any) => {
      const payload = {
        email,
        otp,
        newPassword: values.newPassword,
      };
      dispatch(updatePassword(payload, navigate));
    },
  });

  React.useEffect(() => {
    const bodyElement = document.body;

    bodyElement.classList.add(
      "flex",
      "items-center",
      "justify-center",
      "min-h-screen",
      "py-16",
      "lg:py-10",
      "bg-slate-50",
      "dark:bg-zink-800",
      "dark:text-zink-100",
      "font-public"
    );

    return () => {
      bodyElement.classList.remove(
        "flex",
        "items-center",
        "justify-center",
        "min-h-screen",
        "py-16",
        "lg:py-10",
        "bg-slate-50",
        "dark:bg-zink-800",
        "dark:text-zink-100",
        "font-public"
      );
    };
  }, []);

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="relative">
        {/* <AuthIcon /> */}

        <div className="mb-0 w-screen lg:w-[500px] card shadow-lg border-none shadow-slate-100 relative">
          <div className="!px-10 !py-12 card-body">
            <Link to="/">
              <img
                src={logoLight}
                alt=""
                className="hidden h-6 mx-auto dark:block"
              />
              <img
                src={logoDark}
                alt=""
                className="block h-6 mx-auto dark:hidden"
              />
            </Link>

            <div className="mt-8 text-center">
              <h4 className="mb-1 text-custom-500 dark:text-custom-500">
                Set Your New Password
              </h4>
              <p className="text-slate-500 dark:text-zink-200">
                Please enter and set your new password to complete the update
                process.
              </p>
            </div>

            <form
              className="mt-10"
              id="updatePasswordForm"
              onSubmit={validation.handleSubmit}
            >
              {success && (
                <div className="px-4 py-3 mb-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50">
                  Your password has been updated successfully.
                </div>
              )}
              {error && (
                <div className="px-4 py-3 mb-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50">
                  Unable to update your password. Please try again.
                </div>
              )}

              <div className="mb-3">
                <label
                  htmlFor="new-password-field"
                  className="inline-block mb-2 text-base font-medium"
                >
                  New Password
                </label>
                <input
                  type="text"
                  id="new-password-field"
                  name="newPassword"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Enter your new password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
                {validation.touched.newPassword &&
                validation.errors.newPassword ? (
                  <div
                    id="newPassword-error"
                    className="mt-1 text-sm text-red-500"
                  >
                    {validation.errors.newPassword}
                  </div>
                ) : null}
              </div>

              <div className="mt-10 flex justify-center">
                {isLoading ? (
                  <button
                    type="button"
                    className="flex items-center text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  >
                    <Loader2 className="w-full size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                    Updating...
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="w-full text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    disabled={isLoading}
                  >
                    Confirm Password
                  </button>
                )}
              </div>

              <div className="mt-10 text-center">
                <p className="mb-0 text-slate-500 dark:text-zink-200">
                  <Link
                    to="/login"
                    className="font-semibold transition-all duration-150 ease-linear text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500"
                  >
                    Back to login
                  </Link>{" "}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default UpdatePassword;
